/**
 * Firebase initialization and configuration
 * 
 * This file centralizes all Firebase setup to avoid circular dependencies
  * and make Firebase services available throughout the application.
 * and make Firebase services available throughout the application.
 */
import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent, setAnalyticsCollectionEnabled, Analytics } from 'firebase/analytics';
// import { getPerformance } from 'firebase/performance';
import { PROJECT_ID } from './utils/environmentConfig';
import { initializeEmulators, shouldUseEmulators } from './utils/firebaseEmulator';
import { logger } from './utils/logger';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: `${PROJECT_ID}.firebaseapp.com`,
  projectId: PROJECT_ID,
  storageBucket: `${PROJECT_ID}.firebasestorage.app`,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Analytics instance will be initialized on-demand
let analyticsInstance: Analytics | null = null;

/**
 * Initializes Firebase Analytics when user consents
 * Called from ConditionalScriptLoader when user gives analytics consent
 */
export const initializeFirebaseAnalytics = () => {
  try {
    logger.info('🔍 Initializing Firebase Analytics');
    
    // Don't initialize analytics in development environment
    if (process.env.NODE_ENV === 'development') {
      logger.info('📊 Analytics disabled in development environment.');
      return true; // Return true to avoid errors, but don't actually initialize
    }
    
    analyticsInstance = getAnalytics(app);
    setAnalyticsCollectionEnabled(analyticsInstance, true);
    logEvent(analyticsInstance, 'analytics_consent_given');
    return true;
  } catch (error) {
    logger.error('❌ Failed to initialize Firebase Analytics:', error);
    return false;
  }
};

/**
 * Disables Firebase Analytics when user revokes consent
 * Called from ConditionalScriptLoader when user revokes analytics consent
 */
export const cleanupFirebaseAnalytics = () => {
  if (analyticsInstance) {
    try {
      logger.info('🧹 Disabling Firebase Analytics collection');
      setAnalyticsCollectionEnabled(analyticsInstance, false);
      return true;
    } catch (error) {
      logger.error('❌ Failed to disable Firebase Analytics:', error);
      return false;
    }
  }
  return true;
};

// Initialize emulators only in development and when explicitly enabled
if (shouldUseEmulators()) {
  logger.info('🔧 Initializing Firebase emulators for local development');
  initializeEmulators();
} else {
  logger.info('🚀 Running with production Firebase configuration');
}

export { app };
